import type { SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { CreationSourceEnum, SourceEnum } from 'js/src/client';
import { getVideoCreationSource } from 'js/src/data/app';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    getVoicedrivenSceneDuration,
    runVoicedrivenTranscriptBatchOperation,
} from 'js/src/luminary/voicedriven-scenes';

// Returns the playbackRate override to apply to a clip so it exactly fills its
// scene without looping, or null when the inputs are degenerate (skip). A rate
// below 1 slows the clip down to fill a longer scene; a rate of 1 pins natural
// speed (the clip is longer than the scene and simply truncates). The lower
// bound mirrors luminary's playbackRate setter, which rejects values below
// 0.0625 — without the clamp an extreme scene/clip ratio would throw.
export function computePlaybackRateOverride(
    clipDurationMs: number,
    sceneDurationMs: number,
): number | null {
    if (sceneDurationMs <= 0 || clipDurationMs <= 0) {
        return null;
    }
    return Math.min(1, Math.max(0.0625, clipDurationMs / sceneDurationMs));
}

// Refits each editable clip's playbackRate override so it fills its scene exactly, in both
// directions: a clip slowed for a long voiceover returns to natural speed when a later one is
// shorter. A GenX video's clips are all generated but carry no source tag, hence
// `allClipsAreAi`; otherwise stock and uploaded footage is left to the player. MUST run inside
// runVoicedrivenTranscriptBatchOperation so scene durations are settled.
export function syncAiClipPlaybackRatesForSceneGroup(
    sceneGroup: SceneGroup,
    { allClipsAreAi }: { allClipsAreAi: boolean },
): void {
    for (const scene of sceneGroup.scenes) {
        const sceneDurationMs = getVoicedrivenSceneDuration(scene);
        for (const asset of scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE)) {
            if (!asset.isEditable || !asset.isVideo || asset.duration <= 0) {
                continue;
            }
            if (!allClipsAreAi && asset.sourceData?.source !== SourceEnum.SOURCE_AI_GENERATED) {
                continue;
            }
            const playbackRate = computePlaybackRateOverride(asset.duration, sceneDurationMs);
            if (playbackRate !== null) {
                asset.playbackRate = playbackRate;
            }
        }
    }
}

// After a voice swap or transcript regen grows scene durations, stale playbackRate overrides
// no longer fill their scenes and luminary loops the clips. Refits them without regenerating.
export const syncAiClipPlaybackRatesOperation = createLuminaryOperation(
    async ({ luminary, getState, call }, sceneGroupId: string) => {
        const source = getVideoCreationSource(getState());
        const isGenx =
            source === CreationSourceEnum.GENX || source === CreationSourceEnum.GENX_REPEAT;
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        // Clip durations are only known once the underlying media has loaded.
        await sceneGroup.root.onCanPlay();
        call(runVoicedrivenTranscriptBatchOperation, sceneGroupId, () => {
            syncAiClipPlaybackRatesForSceneGroup(sceneGroup, { allClipsAreAi: isGenx });
        });
    },
);
